import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Can } from '../../shell/Can.js';
import { useTenantIdentity } from '../../shell/useTenantIdentity.js';
import {
  listRolesByStatus,
  cloneRole,
  deactivateRole,
  archiveRole,
  ROLE_STATUS_LABELS,
  type Role,
} from '../../api/rolesApi.js';

const STATUS_FILTERS = [
  { value: 'ACTIVE', labelKey: 'roles.list.status.active' },
  { value: 'INACTIVE', labelKey: 'roles.list.status.inactive' },
  { value: 'ARCHIVED', labelKey: 'roles.list.status.archived' },
  { value: 'ALL', labelKey: 'roles.list.status.all' },
] as const;

export function RoleListPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  // A role means the same thing in every tenant, so only the platform
  // administrator authors one. A tenant administrator sets what a role may do,
  // on the Role Rights screen.
  const authoring = useTenantIdentity(token)?.isPlatformAdmin === true;
  const [rows, setRows] = useState<readonly Role[] | null>(null);
  const [statusFilter, setStatusFilter] = useState('ACTIVE');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Inline clone form state (per row).
  const [cloneFor, setCloneFor] = useState<string | null>(null);
  const [cloneCode, setCloneCode] = useState('');
  const [cloneName, setCloneName] = useState('');
  const [cloneError, setCloneError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setRows(null);
    listRolesByStatus(statusFilter, token)
      .then((r) => { if (alive) setRows(r); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token, statusFilter]);

  async function reload() {
    setRows(await listRolesByStatus(statusFilter, token));
  }

  async function doDeactivate(role: Role) {
    setError(null); setNotice(null); setBusy(true);
    try {
      await deactivateRole(role.roleId, token);
      setNotice(t('roles.list.deactivated', { name: role.roleName }));
      await reload();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }

  async function doArchive(role: Role) {
    setError(null); setNotice(null); setBusy(true);
    try {
      await archiveRole(role.roleId, token);
      setNotice(t('roles.list.archived', { name: role.roleName }));
      await reload();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }

  function openClone(role: Role) {
    setCloneFor(role.roleId);
    setCloneCode(`${role.roleCode}_COPY`);
    setCloneName(`${role.roleName} (copy)`);
    setCloneError(null);
  }

  async function submitClone(role: Role) {
    setCloneError(null);
    if (!/^[A-Z][A-Z0-9_]{1,63}$/.test(cloneCode)) {
      setCloneError(t('roles.list.clone.codePattern'));
      return;
    }
    if (!cloneName.trim()) {
      setCloneError(t('roles.list.clone.nameRequired'));
      return;
    }
    setBusy(true);
    try {
      await cloneRole(role.roleId, cloneCode, cloneName.trim(), token);
      setNotice(t('roles.list.clonedEverywhere', { name: role.roleName, code: cloneCode }));
      setCloneFor(null);
      await reload();
    } catch (e) { setCloneError((e as Error).message); }
    finally { setBusy(false); }
  }

  return (
    <main aria-label="role-list">
      <h1>{t('roles.list.title')}</h1>
      {authoring ? (
        <Can path="/admin/roles" action="create">
          <Link to="/admin/roles/new">{t('roles.list.new')}</Link>
        </Can>
      ) : (
        <p data-testid="roles-authoring-note">{t('roles.list.authoringNote')}</p>
      )}
      <Link to="/admin/roles/matrix">{t('roles.list.matrix')}</Link>
      <Link to="/admin/roles/rights">{t('roles.list.rights')}</Link>
      {/* The platform template, kept beside the tenant's own rights because the
          two are easy to confuse and the screen itself spells out which is which. */}
      <Link to="/admin/roles/born-with">{t('roles.list.bornWith')}</Link>

      <label htmlFor="status-filter">{t('roles.list.statusLabel')}</label>
      <select
        id="status-filter"
        aria-label="status-filter"
        value={statusFilter}
        onChange={(e) => setStatusFilter(e.target.value)}
      >
        {STATUS_FILTERS.map((s) => (
          <option key={s.value} value={s.value}>{t(s.labelKey)}</option>
        ))}
      </select>

      {error ? <p role="alert">{error}</p> : null}
      {notice ? <p role="status">{notice}</p> : null}

      {!rows ? (
        <p>{t('roles.list.loading')}</p>
      ) : rows.length === 0 ? (
        <p>{t('roles.list.empty')}</p>
      ) : (
        <table aria-label="role-table">
          <thead>
            <tr>
              <th scope="col">{t('roles.list.col.name')}</th>
              <th scope="col">{t('roles.list.col.code')}</th>
              <th scope="col">{t('roles.list.col.description')}</th>
              <th scope="col">{t('roles.list.col.status')}</th>
              <th scope="col">{t('roles.list.col.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.roleId}>
                <td><Link to={`/admin/roles/${r.roleId}`}>{r.roleName}</Link></td>
                <td>{r.roleCode}</td>
                <td>{r.roleDescription ?? '—'}</td>
                <td>{ROLE_STATUS_LABELS[r.recordStatusCode] ?? r.recordStatusCode}</td>
                <td>
                  {authoring ? (
                    <Can path="/admin/roles" action="create">
                      <button type="button" aria-label={`clone-${r.roleCode}`} onClick={() => openClone(r)} disabled={busy}>
                        {t('roles.list.clone')}
                      </button>
                    </Can>
                  ) : null}
                  {authoring && !r.isSystemRole && r.recordStatusCode === 'ACTIVE' ? (
                    <Can path="/admin/roles" action="edit">
                      <button type="button" aria-label={`deactivate-${r.roleCode}`} onClick={() => doDeactivate(r)} disabled={busy}>
                        {t('roles.list.deactivate')}
                      </button>
                    </Can>
                  ) : null}
                  {authoring && !r.isSystemRole && r.recordStatusCode !== 'ARCHIVED' ? (
                    <Can path="/admin/roles" action="edit">
                      <button type="button" aria-label={`archive-${r.roleCode}`} onClick={() => doArchive(r)} disabled={busy}>
                        {t('roles.list.archive')}
                      </button>
                    </Can>
                  ) : null}

                  {cloneFor === r.roleId ? (
                    <div aria-label={`clone-form-${r.roleCode}`}>
                      {cloneError ? <p role="alert">{cloneError}</p> : null}
                      <label htmlFor={`clone-code-${r.roleId}`}>{t('roles.list.clone.newCode')}</label>
                      <input
                        id={`clone-code-${r.roleId}`}
                        value={cloneCode}
                        onChange={(e) => setCloneCode(e.target.value.toUpperCase())}
                      />
                      <label htmlFor={`clone-name-${r.roleId}`}>{t('roles.list.clone.newName')}</label>
                      <input
                        id={`clone-name-${r.roleId}`}
                        value={cloneName}
                        onChange={(e) => setCloneName(e.target.value)}
                      />
                      <button type="button" aria-label={`confirm-clone-${r.roleCode}`} onClick={() => submitClone(r)} disabled={busy}>
                        {t('roles.list.clone.confirm')}
                      </button>
                      <button type="button" onClick={() => setCloneFor(null)} disabled={busy}>{t('roles.list.clone.cancel')}</button>
                    </div>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </main>
  );
}
